import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import * as Popover from "@radix-ui/react-popover";
import * as RadixSelect from "@radix-ui/react-select";
import { Check, ChevronDown, Clock, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { findItems } from "@/lib/textsearch";
import { decimalsFromStep, displayNumeric, editNumeric, isAllowedNumericKey } from "@/lib/numeric";
import { clearSegment, emptyTime, formatTime, parseTime, segmentAt, segmentCount, segmentRange, stepSegment, typeDigit, type TimeParts } from "@/lib/timefield";

/** Forma elementlari (redesign v2): to'ldirilgan maydon, fokusda oq yuza va sariq halqa */
const control =
  "w-full rounded-control border border-transparent bg-fill px-3 text-[13px] text-ink placeholder:text-muted transition-[background-color,border-color,box-shadow] duration-150 " +
  "focus:border-accent focus:bg-surface focus:outline-none focus:ring-2 focus:ring-accent/25 disabled:cursor-not-allowed disabled:opacity-60 " +
  "aria-[invalid=true]:border-danger aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-danger/15";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...props },
  ref,
) {
  return <input ref={ref} className={cn(control, "h-9", className)} {...props} />;
});

/**
 * Raqam maydoni (2026-10-18, foydalanuvchi qoidasi: brauzer elementlari yo'q). `type="number"` o'rniga: strelkalar yo'q,
 * harf va "e"/"+" kiritilmaydi (lib/numeric.ts), telefonda raqam klaviaturasi. API `Input` bilan bir xil: `value` -- xom
 * raqam matni yoki raqam, `onChange(e.target.value)` -- xom matn ("1234567.5"). Maydonda esa yozish paytida ming
 * ajratgich ko'rinadi ("1,234,567.5" -- `formatMoney` bilan bir xil format); kursor yozilgan raqamdan keyin qoladi,
 * ajratgich ustida Backspace oldingi raqamni o'chiradi, yozilgan vergul kasr belgisi, qo'yilgan "1 234,50" ham o'qiladi.
 * `group={false}` -- kod/yil kabi ajratgichsiz raqamli matn. Kasr xonalari `decimals` yoki `step="0.01"` dan, minus --
 * `min` 0 dan kichik yoki berilmagan bo'lsa (`allowNegative` bilan aniq aytish mumkin).
 */
export interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "inputMode" | "value" | "defaultValue"> {
  value?: string | number | null;
  defaultValue?: string | number | null;
  decimals?: number;
  allowNegative?: boolean;
  /** Ming ajratgich bilan ko'rsatish (standart yoqiq) */
  group?: boolean;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { className, decimals, allowNegative, group = true, min, max: _max, step, value, defaultValue, onChange, onKeyDown, onPaste, ...props },
  ref,
) {
  void _max; // brauzer cheklovi ishlatilmaydi: tekshiruv forma/backend'da
  const rules = { decimals: decimals ?? decimalsFromStep(step), negative: allowNegative ?? (min === undefined || Number(min) < 0) };
  const display = value === undefined ? undefined : displayNumeric(value, rules, group);
  const initial = defaultValue === undefined ? undefined : displayNumeric(defaultValue, rules, group);
  const prev = useRef(display ?? initial ?? "");
  if (display !== undefined) prev.current = display;
  const pasted = useRef<string | null>(null);
  return (
    <input
      ref={ref}
      type="text"
      inputMode={rules.decimals > 0 ? "decimal" : "numeric"}
      autoComplete="off"
      className={cn(control, "h-9 tabular-nums", className)}
      value={display}
      defaultValue={initial}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (!e.defaultPrevented && !isAllowedNumericKey(e.key, rules, e.ctrlKey || e.metaKey || e.altKey)) e.preventDefault();
      }}
      onPaste={(e) => {
        onPaste?.(e);
        pasted.current = e.clipboardData.getData("text");
      }}
      onChange={(e) => {
        const input = e.target;
        const native = e.nativeEvent as Partial<InputEvent>;
        const r = editNumeric(
          { text: input.value, caret: input.selectionStart ?? input.value.length, prev: prev.current, inputType: native.inputType, data: native.data, pasted: pasted.current },
          rules,
          group,
        );
        pasted.current = null;
        prev.current = r.display;
        input.value = r.raw; // iste'molchi (`e.target.value`) xom qiymatni o'qiydi
        onChange?.(e);
        input.value = r.display; // maydonda ajratgichlar bilan; React qayta chizganda qiymat shu bo'ladi -- kursor joyida qoladi
        input.setSelectionRange(r.caret, r.caret);
      }}
      {...props}
    />
  );
});

/**
 * Vaqt maydoni (2026-10-18, brauzer elementlari yo'q): `type="time"` o'rniga -- doim 24 soat ("23:07:56"), brauzer tiliga
 * bog'liq emas (inglizcha brauzer 12 soatlik "11:07 PM" chizardi), tor joyda kesilmaydi. API native'dek: `value` -- "HH:MM"
 * yoki `seconds` bilan "HH:MM:SS", `onChange(e.target.value)` -- shu format. Yozish segmentlar bo'yicha (lib/timefield.ts):
 * raqam segmentni to'ldirib keyingisiga o'tadi, ↑/↓ qadam, ←/→ segment, Backspace -- 00, qo'yilgan "9:30" ham o'qiladi;
 * telefonda raqam klaviaturasi. `className` tashqi o'ramga (kenglik, chekka), qolgan atributlar maydonning o'ziga.
 */
export interface TimeInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "inputMode" | "value" | "defaultValue"> {
  value: string;
  /** Soniya ham: "HH:MM:SS" */
  seconds?: boolean;
}

export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(function TimeInput(
  { className, value, seconds = false, onChange, onKeyDown, onPaste, onFocus, onBlur, onClick, onPointerDown, ...props },
  ref,
) {
  const seg = useRef(0);
  const pending = useRef("");
  const byPointer = useRef(false);
  const n = segmentCount(seconds);
  const parts = parseTime(value, seconds);
  const display = parts ? formatTime(parts, seconds) : emptyTime(seconds);
  const current = (): TimeParts => parts ?? [0, 0, 0];

  function select(input: HTMLInputElement, i: number) {
    seg.current = Math.max(0, Math.min(n - 1, i));
    const [a, b] = segmentRange(seg.current);
    input.setSelectionRange(a, b);
  }
  /** Yangi qiymatni maydonga yozib, iste'molchiga xabar berish; keyin kursor kerakli segmentda */
  function commit(input: HTMLInputElement, next: TimeParts, moveTo = seg.current, event?: ChangeEvent<HTMLInputElement>) {
    input.value = formatTime(next, seconds); // iste'molchi `e.target.value` ni o'qiydi; React qayta chizganda qiymat shu bo'ladi
    onChange?.(event ?? ({ target: input, currentTarget: input, type: "change" } as unknown as ChangeEvent<HTMLInputElement>));
    select(input, moveTo);
  }
  function typed(input: HTMLInputElement, digit: string, event?: ChangeEvent<HTMLInputElement>) {
    const r = typeDigit(current(), seg.current, pending.current, digit);
    pending.current = r.pending;
    commit(input, r.parts, r.advance ? seg.current + 1 : seg.current, event);
  }

  return (
    <span className={cn("relative inline-flex shrink-0", seconds ? "w-[112px]" : "w-[92px]", className)}>
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        className={cn(control, "h-9 pr-8 tabular-nums", !parts && "text-muted")}
        value={display}
        onPointerDown={(e) => {
          onPointerDown?.(e);
          byPointer.current = true;
        }}
        onFocus={(e) => {
          onFocus?.(e);
          if (!byPointer.current) select(e.currentTarget, 0);
        }}
        onClick={(e) => {
          onClick?.(e);
          byPointer.current = false;
          pending.current = "";
          select(e.currentTarget, segmentAt(e.currentTarget.selectionStart ?? 0, seconds));
        }}
        onBlur={(e) => {
          onBlur?.(e);
          pending.current = "";
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return; // nusxa olish / qo'yish
          const input = e.currentTarget;
          if (/^\d$/.test(e.key)) {
            e.preventDefault();
            typed(input, e.key);
            return;
          }
          switch (e.key) {
            case "ArrowUp":
            case "ArrowDown":
              e.preventDefault();
              pending.current = "";
              commit(input, stepSegment(current(), seg.current, e.key === "ArrowUp" ? 1 : -1));
              return;
            case "ArrowLeft":
            case "ArrowRight":
            case ":":
              e.preventDefault();
              pending.current = "";
              select(input, seg.current + (e.key === "ArrowLeft" ? -1 : 1));
              return;
            case "Backspace":
            case "Delete":
              e.preventDefault();
              pending.current = "";
              commit(input, clearSegment(current(), seg.current));
              return;
            case "Home":
            case "End":
              e.preventDefault();
              select(input, e.key === "Home" ? 0 : n - 1);
              return;
          }
          if (e.key.length === 1) e.preventDefault(); // harf va belgilar kirmaydi
        }}
        onChange={(e) => {
          // Klaviatura hodisasisiz kiritish (telefon klaviaturasi): kiritilgan raqamni segmentga yozamiz, qolganini qaytaramiz
          const input = e.target;
          const native = e.nativeEvent as Partial<InputEvent>;
          if (native.inputType === "insertText" && native.data && /^\d$/.test(native.data)) {
            typed(input, native.data, e);
          } else if (native.inputType === "deleteContentBackward" || native.inputType === "deleteContentForward") {
            pending.current = "";
            commit(input, clearSegment(current(), seg.current), seg.current, e);
          } else {
            input.value = display;
            select(input, seg.current);
          }
        }}
        onPaste={(e) => {
          onPaste?.(e);
          if (e.defaultPrevented) return;
          e.preventDefault();
          const p = parseTime(e.clipboardData.getData("text"), seconds);
          if (p) {
            pending.current = "";
            commit(e.currentTarget, p, 0);
          }
        }}
        {...props}
      />
      <Clock size={14} strokeWidth={1.75} aria-hidden className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-muted" />
    </span>
  );
});

/**
 * Tanlov (2026-10-18): brauzerning native ro'yxati o'rniga o'z dizaynimizdagi ochiluvchi ro'yxat (Radix Select).
 * API native `<select>` bilan bir xil qoladi: `value`, `onChange(e.target.value)`, bolalar `<option>` / `<optgroup>` --
 * chaqiruvchi kod o'zgarmaydi. Bo'sh qiymat ("Tanlang…") ham oddiy band: Radix bo'sh satrni qabul qilmaydi, ichkarida belgi.
 */
export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}
export interface SelectGroup {
  label?: ReactNode;
  options: SelectOption[];
}

function textOf(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children);
  return "";
}

/** `<option>` / `<optgroup>` (va Fragment) bolalaridan guruhlangan ro'yxat */
export function parseOptions(children: ReactNode): SelectGroup[] {
  const groups: SelectGroup[] = [];
  let current: SelectGroup | null = null;
  const walk = (nodes: ReactNode) => {
    Children.forEach(nodes, (node) => {
      if (!isValidElement(node)) return;
      const props = node.props as { value?: unknown; children?: ReactNode; disabled?: boolean; label?: ReactNode };
      if (node.type === "option") {
        if (!current) {
          current = { options: [] };
          groups.push(current);
        }
        current.options.push({ value: String(props.value ?? textOf(props.children)), label: props.children, disabled: props.disabled });
      } else if (node.type === "optgroup") {
        const group: SelectGroup = { label: props.label, options: [] };
        groups.push(group);
        current = group;
        walk(props.children);
        current = null;
      } else if (props.children !== undefined) {
        walk(props.children);
      }
    });
  };
  walk(children);
  return groups;
}

const EMPTY = "__empty__";
const toRadix = (v: string) => (v === "" ? EMPTY : v);
const fromRadix = (v: string) => (v === EMPTY ? "" : v);

/** Shundan ko'p variant bo'lsa ro'yxat tepasida qidiruv (2026-10-28, foydalanuvchi: "optionlar juda ko'p bo'lsa topish qiyin") */
export const SEARCH_MIN_OPTIONS = 10;

/**
 * Qidiruv natijasi (`lib/textsearch`, backend bilan bir xil qoida): tutuq belgilari, urg'u, kirill farq qilmaydi, so'zlar tartibi
 * muhim emas; aniq topilmasa -- o'xshashlari (eng yaqini birinchi, guruhsiz, `similar`). Bo'sh qiymatli "Hammasi/Tanlang" --
 * faqat qidiruvsiz.
 */
export function filterGroups(groups: SelectGroup[], query: string): { groups: SelectGroup[]; similar: boolean } {
  if (!query.trim()) return { groups, similar: false };
  const flat = groups.flatMap((g) => g.options.filter((o) => o.value !== ""));
  const found = findItems(flat, query, (o) => textOf(o.label));
  if (found.similar) return { groups: [{ options: found.items }], similar: true };
  const keep = new Set(found.items);
  return { groups: groups.map((g) => ({ ...g, options: g.options.filter((o) => keep.has(o)) })).filter((g) => g.options.length > 0), similar: false };
}

/** Trigger -- tugma (Radix), shuning uchun hodisa turlari tugmaniki; `onChange` esa native select'dek `e.target.value` beradi */
type TriggerProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "value" | "defaultValue" | "type">;
export interface SelectProps extends TriggerProps {
  value?: string | number | readonly string[];
  defaultValue?: string | number | readonly string[];
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  required?: boolean;
  placeholder?: string;
  /** Bo'sh qiymat tanlanganda matn xira (forma maydoni); filtrda o'chiriladi */
  muteEmpty?: boolean;
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  { className, children, value, defaultValue, onChange, name, disabled, required, id, placeholder, muteEmpty = true, ...rest },
  ref,
) {
  const [inner, setInner] = useState(() => String(defaultValue ?? ""));
  const current = value === undefined ? inner : String(value);
  const groups = parseOptions(children);

  function change(raw: string) {
    const next = fromRadix(raw);
    if (value === undefined) setInner(next);
    const target = { value: next, name } as unknown as EventTarget & HTMLSelectElement;
    onChange?.({ target, currentTarget: target, type: "change" } as unknown as ChangeEvent<HTMLSelectElement>);
  }

  const count = groups.reduce((n, g) => n + g.options.filter((o) => o.value !== "").length, 0);
  if (count > SEARCH_MIN_OPTIONS) {
    return (
      <SearchSelect
        ref={ref}
        id={id}
        groups={groups}
        current={current}
        onPick={change}
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        className={cn(control, "inline-flex h-9 cursor-pointer items-center justify-between gap-2 pr-2.5 text-left", muteEmpty && current === "" && "text-muted", className)}
        triggerProps={rest}
      />
    );
  }

  return (
    <RadixSelect.Root value={toRadix(current)} onValueChange={change} name={name} disabled={disabled} required={required}>
      <RadixSelect.Trigger
        ref={ref}
        id={id}
        className={cn(control, "inline-flex h-9 cursor-pointer items-center justify-between gap-2 pr-2.5 text-left", muteEmpty && current === "" && "text-muted", className)}
        {...rest}
      >
        <span className="truncate">
          <RadixSelect.Value placeholder={placeholder} />
        </span>
        <RadixSelect.Icon asChild>
          <ChevronDown size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          className="fade-in z-[70] max-h-[min(360px,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] max-w-[min(480px,92vw)] overflow-hidden rounded-2xl border border-border/60 bg-surface p-1.5 text-[13px] text-ink shadow-menu"
        >
          <RadixSelect.ScrollUpButton className="flex h-6 cursor-default items-center justify-center text-muted">
            <ChevronDown size={14} className="rotate-180" aria-hidden />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport>
            {groups.map((g, gi) => (
              <RadixSelect.Group key={gi}>
                {g.label !== undefined && <RadixSelect.Label className="px-2.5 pt-1.5 pb-1 text-[11px] font-semibold tracking-wide text-faint uppercase">{g.label}</RadixSelect.Label>}
                {g.options.map((o) => (
                  <RadixSelect.Item
                    key={`${gi}-${o.value}`}
                    value={toRadix(o.value)}
                    disabled={o.disabled}
                    className={cn(
                      "relative flex cursor-pointer items-center rounded-lg py-1.5 pr-8 pl-2.5 outline-none select-none data-[highlighted]:bg-fill data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                      o.value === "" && "text-muted",
                    )}
                  >
                    <RadixSelect.ItemText>{o.label}</RadixSelect.ItemText>
                    <RadixSelect.ItemIndicator className="absolute right-2.5 inline-flex">
                      <Check size={14} strokeWidth={2} aria-hidden />
                    </RadixSelect.ItemIndicator>
                  </RadixSelect.Item>
                ))}
              </RadixSelect.Group>
            ))}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className="flex h-6 cursor-default items-center justify-center text-muted">
            <ChevronDown size={14} aria-hidden />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
});

/**
 * Ko'p variantli tanlov (2026-10-28): xuddi shu ko'rinishdagi tugma, ochilganda tepada qidiruv maydoni va ro'yxat. Klaviatura:
 * yozish -- qidiruv, ↑/↓ -- tanlash, Enter -- tanlash, Esc -- yopish. Radix Select ichiga maydon qo'yib bo'lmaydi (u harflarni o'zi
 * ushlaydi), shuning uchun Popover + listbox. API `Select` bilan bir xil -- chaqiruvchi kod o'zgarmaydi.
 */
const SearchSelect = forwardRef<
  HTMLButtonElement,
  {
    id?: string;
    groups: SelectGroup[];
    current: string;
    onPick: (value: string) => void;
    name?: string;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    triggerProps: TriggerProps;
  }
>(function SearchSelect({ id, groups, current, onPick, name, disabled, placeholder, className, triggerProps }, ref) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const { groups: shown, similar } = useMemo(() => filterGroups(groups, query), [groups, query]);
  const flat = useMemo(() => shown.flatMap((g) => g.options).filter((o) => !o.disabled), [shown]);
  const selected = groups.flatMap((g) => g.options).find((o) => o.value === current);

  // Ochilganda -- tanlangan variant (bo'lmasa birinchisi) faol; qidiruv o'zgarsa -- birinchi topilgan
  useEffect(() => {
    if (!open) return;
    const i = query ? 0 : flat.findIndex((o) => o.value === current);
    setActive(Math.max(0, i));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);
  useEffect(() => {
    if (open) listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  function pick(value: string) {
    onPick(value);
    setOpen(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (flat.length) setActive((i) => (e.key === "ArrowDown" ? Math.min(flat.length - 1, i + 1) : Math.max(0, i - 1)));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flat[active]) pick(flat[active].value);
    }
  }

  let index = -1;
  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
      modal
    >
      <Popover.Trigger
        ref={ref}
        id={id}
        type="button"
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={className}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        {...triggerProps}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
      </Popover.Trigger>
      {name && <input type="hidden" name={name} value={current} />}
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onKeyDown={onKey}
          className="fade-in z-[70] flex max-h-[min(400px,var(--radix-popover-content-available-height))] min-w-[max(var(--radix-popover-trigger-width),220px)] max-w-[min(480px,92vw)] flex-col overflow-hidden rounded-2xl border border-border/60 bg-surface p-1.5 text-[13px] text-ink shadow-menu"
        >
          <div className="relative mb-1 shrink-0">
            <Search size={14} strokeWidth={1.75} aria-hidden className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-muted" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("select.search")}
              aria-label={t("select.search")}
              aria-controls={id ? `${id}-list` : undefined}
              className="h-8 w-full rounded-lg bg-fill pr-2.5 pl-8 text-[13px] text-ink outline-none placeholder:text-muted focus:ring-2 focus:ring-accent/25"
            />
          </div>
          <div ref={listRef} id={id ? `${id}-list` : undefined} role="listbox" className="min-h-0 overflow-y-auto">
            {similar && <div className="px-2.5 pt-1 pb-1.5 text-[11px] text-muted">{t("select.similar")}</div>}
            {shown.length === 0 ? (
              <div className="px-2.5 py-2 text-muted">{t("states.noResults")}</div>
            ) : (
              shown.map((g, gi) => (
                <div key={gi} role="group">
                  {g.label !== undefined && <div className="px-2.5 pt-1.5 pb-1 text-[11px] font-semibold tracking-wide text-faint uppercase">{g.label}</div>}
                  {g.options.map((o) => {
                    const i = o.disabled ? -1 : ++index;
                    const isActive = i === active && i >= 0;
                    return (
                      <div
                        key={`${gi}-${o.value}`}
                        role="option"
                        aria-selected={o.value === current}
                        aria-disabled={o.disabled || undefined}
                        data-index={i >= 0 ? i : undefined}
                        onMouseMove={() => i >= 0 && i !== active && setActive(i)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => !o.disabled && pick(o.value)}
                        className={cn(
                          "relative flex cursor-pointer items-center rounded-lg py-1.5 pr-8 pl-2.5 select-none",
                          isActive && "bg-fill",
                          o.value === "" && "text-muted",
                          o.disabled && "pointer-events-none opacity-50",
                        )}
                      >
                        <span className="truncate">{o.label}</span>
                        {o.value === current && <Check size={14} strokeWidth={2} aria-hidden className="absolute right-2.5" />}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});

/**
 * Ro'yxat filtri tanlovi: qiymat tanlanganda sariq chegara va yumshoq sariq fon -- qaysi filtr turgani bir qarashda
 * ko'rinsin (foydalanuvchi talabi, 2026-10-17). `emptyValue` -- "filtr yo'q" degan qiymat (odatda bo'sh satr).
 */
export const FilterSelect = forwardRef<HTMLButtonElement, SelectProps & { emptyValue?: string }>(function FilterSelect(
  { className, value, emptyValue = "", ...props },
  ref,
) {
  const active = value != null && String(value) !== emptyValue;
  return <Select ref={ref} value={value} muteEmpty={false} data-active={active || undefined} className={cn(active && "border-accent bg-accent-soft", className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} className={cn(control, "min-h-[80px] py-2", className)} {...props} />;
  },
);

export function Label({ children, required, htmlFor, className }: { children: ReactNode; required?: boolean; htmlFor?: string; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={cn("mb-1 block text-[12px] font-medium text-ink-soft", className)}>
      {children}
      {required && <span className="text-danger"> *</span>}
    </label>
  );
}

export interface FieldProps {
  label: string;
  required?: boolean;
  error?: string | string[];
  hint?: string;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

/** Forma maydoni: nom, boshqaruv elementi, izoh yoki xato */
export function Field({ label, required, error, hint, htmlFor, children, className }: FieldProps) {
  const message = Array.isArray(error) ? error.join(" ") : error;
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} required={required}>
        {label}
      </Label>
      {children}
      {message ? (
        <p className="mt-1 text-[12px] text-danger">{message}</p>
      ) : hint ? (
        <p className="mt-1 text-[12px] text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export function Checkbox({ label, className, ...props }: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode }) {
  return (
    <label className={cn("inline-flex cursor-pointer items-center gap-2 text-[13px]", className)}>
      <input type="checkbox" className="h-4 w-4 rounded border-border accent-primary" {...props} />
      {label}
    </label>
  );
}
